export const paperUrl = "https://arxiv.org/abs/2603.29761";

export const aboutSections = {
  sectionIds: [
    "overview",
    "maia",
    "bottleneck",
    "results",
    "risks",
    "sft",
    "future",
    "story",
    "developers",
  ],
  hero: {
    eyebrow: "About Catie",
    title: "Catie predicts chess like language.",
    body:
      "Catie reads the whole game as a move-token sequence, then predicts the next move directly. No Stockfish. No tree search. No engine lines hidden behind the interface.",
  },
} as const;

export const aboutNavigation = [
  { id: "overview", label: "Overview" },
  { id: "maia", label: "Maia" },
  { id: "bottleneck", label: "Tracking" },
  { id: "results", label: "Results" },
  { id: "risks", label: "Risks" },
  { id: "sft", label: "SFT" },
  { id: "future", label: "Future" },
  { id: "story", label: "Story" },
  { id: "developers", label: "Team" },
] as const;

export const aboutHeroSignals = [
  "Game history is the input",
  "The next move is the token",
  "Human-like behavior is the object",
] as const;

export const aboutHeroTokens = ["e2e4", "c7c5", "g1f3", "d7d6"] as const;

export const aboutPredictionBars = [
  { move: "d2d4", label: "open center", value: 84 },
  { move: "f1b5", label: "pin pressure", value: 58 },
  { move: "b1c3", label: "develop", value: 42 },
] as const;

export const aboutFigureChecks = [
  { value: "0", label: "engine nodes" },
  { value: "0", label: "searched lines" },
  { value: "1", label: "next token" },
] as const;

export const aboutMetrics = [
  {
    value: "2570",
    label: "Lichess bullet",
    detail: "Final 120M searchless model over 253 rated games.",
  },
  {
    value: "55.2%",
    label: "Top-1 human move prediction",
    detail: "Native-input evaluation; richer sequence context is part of the advantage.",
  },
  {
    value: "0.26%",
    label: "Illegal-move rate",
    detail: "A practical tracking signal for whether Catie still knows the board.",
  },
  {
    value: "120M",
    label: "Final model size",
    detail: "Scaling repaired tracking enough for stronger decision learning.",
  },
] as const;

export const maiaComparison = [
  {
    title: "Maia-style model",
    label: "Position first",
    lines: ["Reads a board position.", "Predicts human moves by rating band.", "Strong static human-move baseline."],
  },
  {
    title: "Catie",
    label: "Game first",
    lines: [
      "Reads the move history.",
      "Predicts the next move as a token.",
      "Can preserve style, momentum, and repetition context.",
    ],
  },
] as const;

export const bottleneckCards = [
  {
    key: "T",
    title: "Tracking",
    body: "Catie must reconstruct the board from the moves. If tracking fails, it may try to move a piece that is no longer there.",
  },
  {
    key: "Q",
    title: "Decision quality",
    body: "Catie must still choose chess moves that feel plausible and strong for the intended player style.",
  },
  {
    key: "P",
    title: "Practical strength",
    body: "The paper frames performance as limited by the weaker capability: P <= min(T, Q).",
  },
] as const;

export const capabilityBars = [
  {
    label: "Tracking",
    value: 92,
    detail: "Can the model reconstruct the board from move history?",
  },
  {
    label: "Decision quality",
    value: 78,
    detail: "Can it choose a plausible, strong move for the intended style?",
  },
  {
    label: "Practical play",
    value: 78,
    detail: "Strength is capped by the weaker capability.",
  },
] as const;

export const issueCards = [
  {
    title: "Piece-blunder risk",
    body: "We acknowledge that our model still blunders due to a) no calculation b) we use bullet games as base model dataset. We are actively seeking ways of improving. Stay tuned!",
  },
  {
    title: "Late-game cliffs",
    body: "Some games have a transition point where severe errors become more likely. The final model pushes this later and makes it rarer, but it is still a real research boundary.",
  },
  {
    title: "Over-weighting collapse",
    body: "Training only toward high-quality moves can quietly damage tracking. Lower validation loss did not always mean stronger play.",
  },
] as const;

export const sftNotes = [
  {
    title: "One base model",
    body: "Catie starts from a broad sequence model trained on move-token games across many rating levels and positions.",
  },
  {
    title: "Many fine-tuned players",
    body: "SFT can adapt that base toward style: aggressive, solid, beginner-like, master-like, or player-specific.",
  },
  {
    title: "Capacity matters",
    body: "Early 28M SFT attempts weakened the model. On the 120M base, a Hikaru-style fine-tune showed a meaningful gain.",
  },
] as const;

export const futureItems = [
  "Improve the base model with better data balance, including stronger blitz coverage.",
  "Split Catie into clearer model families for style, strength, and personality.",
  "Add calibrated probes that reveal model move preferences and possible W/D/L belief.",
  "Use probe signals later to explain decisions in natural language.",
] as const;

export const devStory = [
  {
    step: "01",
    title: "Baseline worked, then drifted",
    body: "The first sequence model could play, but quality degraded deeper into games.",
  },
  {
    step: "02",
    title: "Filtering looked logical and failed",
    body: "Keeping only high-Elo games improved openings but damaged middlegame and endgame tracking.",
  },
  {
    step: "03",
    title: "Scaling repaired tracking",
    body: "Moving from 28M to 120M parameters sharply reduced illegal moves.",
  },
  {
    step: "04",
    title: "Elo weighting found the balance",
    body: "Linear weighting kept messy low-Elo diversity while giving stronger games more influence.",
  },
] as const;

export const aboutDevelopers = [
  {
    name: "Fox Jiang",
    affiliation: "Independent researcher",
    role: "Project Lead & Head Developer",
    initials: "FJ",
  },
  {
    name: "GaGa Long",
    affiliation: "Abbey Park High School",
    role: "Web Developer & Model Developer",
    initials: "GL",
  },
  {
    name: "William Jiang",
    affiliation: "Shanghai Soong Ching Ling School",
    role: "Model Developer",
    initials: "WJ",
  },
] as const;
